import { useState } from 'react';

import {
  Alert,
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  HelpTip,
  Input,
  Label,
  PageHeader,
  Switch,
} from '@/components/ui';
import { useProfiles, useProfileActions } from '@/lib/admin-store';
import type {
  Profile,
  ProfilePermissionKey,
} from '@/lib/admin-types';

/**
 * Página admin /admin/perfis — lista os perfis de uso + diálogo de edição.
 *
 * FEAT-V2-037 Fase A (Cowork 2026-05-25). FASE-C: redesign — PageHeader
 * próprio, cards canônicos e copy sem jargão (o aviso técnico
 * `profile_slug === "tecnico"` saiu da tela; virou "identificador" com
 * explicação em HelpTip).
 *
 * Semântica (inalterada):
 *   - O identificador do perfil é IMUTÁVEL (regra estrutural ADR-012).
 *   - Nome + descrição: editáveis.
 *   - permission_overrides: 3 toggles (pode_ver_tarefas_outros /
 *     pode_inativar_cards / pode_criar_tarefa_pra_outros). É um modelo pra
 *     quem entrar depois — não reaplica em quem já existe.
 */
export function ProfilesPage() {
  const profiles = useProfiles();
  const [editing, setEditing] = useState<Profile | null>(null);

  return (
    <div className="min-w-0">
      <PageHeader
        crumb="Configurações · Pessoas"
        title="Perfis de uso"
        subtitle="Cada perfil define com o que a pessoa já nasce podendo mexer e como o app se organiza no celular. Perfil não é departamento: o departamento diz onde a pessoa trabalha, o perfil diz o que ela faz."
      />

      <Alert variant="info" rail className="mb-6">
        As permissões abaixo valem como <strong>ponto de partida</strong> pra quem for cadastrado
        depois. Quem já tem acesso continua com as permissões atuais — pra mudar, edite a pessoa
        em Usuários e acessos.
      </Alert>

      <div className="grid min-w-0 gap-3">
        {profiles.length === 0 ? (
          <Alert variant="info">Carregando perfis…</Alert>
        ) : (
          profiles.map((p) => (
            <ProfileCard key={p.id} profile={p} onEdit={() => setEditing(p)} />
          ))
        )}
      </div>

      {editing && (
        <ProfileEditDialog
          profile={editing}
          open={!!editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function ProfileCard({ profile, onEdit }: { profile: Profile; onEdit: () => void }) {
  return (
    <Card
      variant="flat"
      data-testid={`profile-card-${profile.slug}`}
      className="min-w-0 bg-paper-warm p-4 dark:bg-paper-dark-warm"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <h3 className="font-display text-xl tracking-tight text-ink-1 dark:text-ink-dark-1">
              {profile.nome}
            </h3>
            <span className="inline-flex items-center gap-1">
              <Badge variant="neutral" size="sm" className="font-mono">
                {profile.slug}
              </Badge>
              <HelpTip texto="Identificador fixo do perfil. Ele não muda porque o sistema o usa internamente pra saber o que liberar. O nome e a descrição, sim, você pode alterar." />
            </span>
          </div>
          {profile.descricao && (
            <p className="mb-3 max-w-[68ch] text-sm text-ink-2 dark:text-ink-dark-2">
              {profile.descricao}
            </p>
          )}
          <PermissionsSummary overrides={profile.permissionOverrides} />
        </div>
        <Button variant="ghost" size="sm" onClick={onEdit}>
          Editar
        </Button>
      </div>
    </Card>
  );
}

const PERMISSION_LABELS: Record<ProfilePermissionKey, string> = {
  pode_ver_tarefas_outros: 'Vê tarefas de outros',
  pode_inativar_cards: 'Inativa cards',
  pode_criar_tarefa_pra_outros: 'Cria tarefa pra outros',
};

function PermissionsSummary({
  overrides,
}: {
  overrides: Partial<Record<ProfilePermissionKey, 0 | 1>>;
}) {
  const entries = (Object.keys(PERMISSION_LABELS) as ProfilePermissionKey[]).map((key) => ({
    key,
    label: PERMISSION_LABELS[key],
    value: overrides[key],
  }));
  // FASE-C: as pílulas eram feitas na mão com ✓ / ✗ / — (emoji como ícone +
  // par de cor no limite do AA). Agora é o <Badge> canônico e o estado vem
  // escrito por extenso, sem símbolo.
  return (
    <div className="flex flex-wrap gap-1.5">
      {entries.map((e) => (
        <Badge
          key={e.key}
          size="sm"
          variant={e.value === 1 ? 'success' : 'neutral'}
          dot={e.value === 1}
        >
          {e.label}: {e.value === 1 ? 'sim' : e.value === 0 ? 'não' : 'não definido'}
        </Badge>
      ))}
    </div>
  );
}

function ProfileEditDialog({
  profile,
  open,
  onClose,
}: {
  profile: Profile;
  open: boolean;
  onClose: () => void;
}) {
  const { updateProfile, pending } = useProfileActions();
  const [nome, setNome] = useState(profile.nome);
  const [descricao, setDescricao] = useState(profile.descricao ?? '');
  const [overrides, setOverrides] = useState<Record<ProfilePermissionKey, 0 | 1>>({
    pode_ver_tarefas_outros: profile.permissionOverrides.pode_ver_tarefas_outros ?? 0,
    pode_inativar_cards: profile.permissionOverrides.pode_inativar_cards ?? 0,
    pode_criar_tarefa_pra_outros: profile.permissionOverrides.pode_criar_tarefa_pra_outros ?? 0,
  });

  async function handleSave() {
    await updateProfile(profile.id, {
      nome,
      descricao: descricao.trim() === '' ? null : descricao.trim(),
      permissionOverrides: overrides,
    });
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} size="md">
      <DialogHeader>
        <DialogTitle>Editar perfil {profile.nome}</DialogTitle>
        <DialogDescription>
          O identificador <strong>{profile.slug}</strong> não muda — o sistema o usa pra saber o
          que liberar pra esse perfil. Você pode alterar o nome, a descrição e as permissões
          iniciais.
        </DialogDescription>
      </DialogHeader>
      <DialogContent>
        <div className="space-y-4">
          <div>
            <Label htmlFor="profile-nome" required>
              Nome
            </Label>
            <Input
              id="profile-nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              autoFocus
              maxLength={120}
            />
          </div>
          <div>
            <Label htmlFor="profile-descricao">Descrição</Label>
            <Input
              id="profile-descricao"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              maxLength={500}
              placeholder="Em que situação esse perfil se aplica…"
            />
          </div>
          <div className="space-y-2 border-t border-line pt-3 dark:border-line-dark">
            <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
              Permissões iniciais deste perfil
            </p>
            <p className="-mt-1 mb-2 text-xs text-ink-2 dark:text-ink-dark-2">
              Valem pra quem for cadastrado a partir de agora. Quem já tem acesso continua como
              está — pra mudar, edite a pessoa em Usuários e acessos.
            </p>
            {(Object.keys(PERMISSION_LABELS) as ProfilePermissionKey[]).map((key) => (
              <Switch
                key={key}
                label={PERMISSION_LABELS[key]}
                checked={overrides[key] === 1}
                onChange={(e) =>
                  setOverrides((prev) => ({ ...prev, [key]: e.target.checked ? 1 : 0 }))
                }
              />
            ))}
          </div>
        </div>
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={pending === profile.id}>
          Cancelar
        </Button>
        <Button onClick={handleSave} loading={pending === profile.id}>
          Salvar
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
